import { AppService } from './../../../../../core/app.service';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from "@angular/material/icon";
import { ActivatedRoute, Router } from '@angular/router';
import { ActiviteService } from '../../../../../services/activite/activite.service';
import { ActivtieResponse } from '../../../../../data/http/responses/activite.response';
import { ExpressionBesoinService } from '../../../../../services/expressionbesoin.service';
import { routeAlias } from '../../../../../../routesalias';
import { ExpressionBesoinReq } from '../../../../../data/http/requests/expression.req';
import { LoaderComponent } from "../../../../../components/ui/loader/loader.component";
import { Input } from "../../../../../components/forms/input/input";
import { StructureService } from '../../../../../services/StructureService';
import { StructureResponse } from '../../../../../data/http/responses/strcuture.res';
import { LigneBudgetaireService } from '../../../../../services/lignebudgetaire/lignebudgetaire.service';
import { LigneBudgetaireResponse } from '../../../../../data/http/responses/lignebudgetaire.res';
import { toSnakeCaseUpper } from '../../../../../utils/helpers';
import { SelectComponent } from '../../../../../components/forms/select/select.component';
import { SelectOption } from '../../../../../data/schemas/selectoption.schema';
import { UiButtonComponent } from '../../../../../components/button/button.component';

@Component({
  selector: 'app-add',
  imports: [ReactiveFormsModule, MatIcon, LoaderComponent, Input, SelectComponent, UiButtonComponent],
  templateUrl: './add.component.html',
  styleUrl: './add.component.css'
})
export class AddExpressionBesoinPage implements OnInit {

  expressionBesoinForm!: FormGroup;
  activities: ActivtieResponse[] = [];

  public isLoading: boolean = false;
  public loadingActivites: boolean = false;
  public loadingStructures: boolean = false;
  public loadingLignesBudgetaires: boolean = false;

  public structureData: SelectOption[] = [];
  public ligneBudgetaires: SelectOption[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private activiteService: ActiviteService,
    private AppService: AppService,
    private ligneBudgetaireService: LigneBudgetaireService,
    private expressionBesoinService: ExpressionBesoinService,
    private structureService: StructureService
  ) {}

  ngOnInit(): void {
    this.expressionBesoinForm = this.fb.group({
      activitesSelectionnees: this.fb.array([]),
      applicationId: this.AppService.getAppId()!,
      complementaryInfo: this.fb.group({
        libelle: [''],
        description: [''],
        supervisor: [''],
        structureResponsable: [''],
        montantEstimatif: [''],
        imputationBudgetaire: [''],
        coutPrevuN1: [''],
        previsionDecaissementN2: [''],
        datePrevision: [''],
        dureePrevisionExecutionMarche: [''],
      })
    });

    this.activitesFormArray.valueChanges.subscribe(selectedActivityIds => {
      this.updateCommonValues(selectedActivityIds);
    });

    this.loadActivities();
    this.addActivity();
    this.loadLigneBudgetaire();
    this.loadStructures();
  }

  get activitesFormArray(): FormArray {
    return this.expressionBesoinForm.get('activitesSelectionnees') as FormArray;
  }

  get complementaryInfoFormGroup(): FormGroup {
    return this.expressionBesoinForm.get('complementaryInfo') as FormGroup;
  }

  addActivity(): void {
    this.activitesFormArray.push(this.fb.control('', Validators.required));
  }

  removeActivity(index: number): void {
    if (this.activitesFormArray.length > 1) {
      this.activitesFormArray.removeAt(index);
    }
  }

  getAvailableActivities(index: number): ActivtieResponse[] {
    const selectedIds = this.activitesFormArray.value;
    const currentId = this.activitesFormArray.at(index).value;
    return this.activities.filter(activity =>
      !selectedIds.includes(activity.id) || activity.id === currentId
    );
  }

  getAvailableActivitiesAsOptions(index: number): SelectOption[] {
    return this.getAvailableActivities(index).map(a => ({
      value: a.id as string,
      label: a.titre ?? `Activité #${a.id}`
    }));
  }

  loadActivities(): void {
    this.loadingActivites = true;
    this.activiteService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response) => {
        this.activities = response.data as ActivtieResponse[];
        this.loadingActivites = false;
      },
      error: (error) => {
        console.error("Erreur lors du chargement des activités", error);
        this.loadingActivites = false;
      }
    });
  }

  loadStructures(): void {
    this.loadingStructures = true;
    this.structureService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response: any) => {
        this.structureData = response.data.map((item: StructureResponse) => ({
          value: item.code,
          label: item?.code
        }));
        this.loadingStructures = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des structures :', error);
        this.loadingStructures = false;
      }
    });
  }

  loadLigneBudgetaire(): void {
    this.loadingLignesBudgetaires = true;
    this.ligneBudgetaireService.getAllByAppId(this.AppService.getAppId()!).subscribe({
      next: (response: any) => {
        this.ligneBudgetaires = response.data.map((item: LigneBudgetaireResponse) => ({
          value: item.libelle,
          label: item?.libelle
        }));
        this.loadingLignesBudgetaires = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des lignes budgétaires :', error);
        this.loadingLignesBudgetaires = false;
      }
    });
  }

  updateCommonValues(selectedActivityIds: string[]): void {
    if (!selectedActivityIds || selectedActivityIds.length === 0) {
      this.resetComplementaryInfo();
      return;
    }
    const selectedActivities = this.activities.filter(activity =>
      selectedActivityIds.includes(activity.id!)
    );
    if (selectedActivities.length === 0) return;
    const commonValues = this.getCommonCharacteristics(selectedActivities);
    this.updateFormWithCommonValues(commonValues);
  }

  getCommonCharacteristics(activities: ActivtieResponse[]): any {
    const commonValues: any = { supervisor: null, structureResponsable: null, imputationBudgetaire: null };
    if (activities.length === 0) return commonValues;

    const supervisors = activities.map(a => this.getCharacteristicValue(a, 'SUPERVISOR'));
    commonValues.supervisor = this.allValuesEqual(supervisors) ? supervisors[0] : '';

    const structures = activities.map(a => this.getCharacteristicValue(a, 'STRUCTURE_RESPONSABLE'));
    commonValues.structureResponsable = this.allValuesEqual(structures) ? structures[0] : '';

    const imputations = activities.map(a => this.getCharacteristicValue(a, 'IMPUTATION_BUDGETAIRE'));
    commonValues.imputationBudgetaire = this.allValuesEqual(imputations) ? imputations[0] : '';

    return commonValues;
  }

  getCharacteristicValue(activity: ActivtieResponse, code: string): string {
    const c = activity.caracteristiques?.find(c => c.code === code);
    return c ? c.valeur : '';
  }

  allValuesEqual(values: string[]): boolean {
    if (values.length === 0) return false;
    const nonEmpty = values.filter(v => v && v.trim() !== '');
    if (nonEmpty.length === 0) return false;
    return nonEmpty.every(v => v === nonEmpty[0]);
  }

  updateFormWithCommonValues(commonValues: any): void {
    const complementaryInfo = this.complementaryInfoFormGroup;
    ['supervisor', 'structureResponsable', 'imputationBudgetaire'].forEach(field => {
      const control = complementaryInfo.get(field);
      if (control && !control.dirty) {
        control.setValue(commonValues[field] || '');
      }
    });
  }

  resetComplementaryInfo(): void {
    this.complementaryInfoFormGroup.patchValue({
      supervisor: '',
      structureResponsable: '',
      imputationBudgetaire: ''
    });
    this.complementaryInfoFormGroup.markAsPristine();
  }

  onSubmit(): void {
    if (this.expressionBesoinForm.valid) {
      const caracteristiquesArray: { code: string; valeur: any }[] = [];
      const excluded = ['libelle', 'description'];
      Object.keys(this.expressionBesoinForm.value.complementaryInfo).forEach(key => {
        if (!excluded.includes(key)) {
          caracteristiquesArray.push({
            code: toSnakeCaseUpper(key),
            valeur: this.expressionBesoinForm.value.complementaryInfo[key] ?? ''
          });
        }
      });

      this.isLoading = true;
      const requestBody: ExpressionBesoinReq = {
        applicationId: this.expressionBesoinForm.value.applicationId,
        activites: this.expressionBesoinForm.value.activitesSelectionnees,
        typeActivite: 'MARCHE',
        titre: this.expressionBesoinForm.value.complementaryInfo.libelle,
        objectif: this.expressionBesoinForm.value.complementaryInfo.description,
        caracteristiques: caracteristiquesArray
      };

      this.expressionBesoinService.create(requestBody).subscribe({
        next: () => {
          this.isLoading = false;
          this.router.navigate([
            routeAlias.marchees.home,
            routeAlias.marchees.expression_besoin.home,
          ]);
        },
        error: (error) => {
          console.error('Erreur lors de la création :', error);
          this.isLoading = false;
        }
      });
    } else {
      console.error('Le formulaire est invalide');
      this.expressionBesoinForm.markAllAsTouched();
    }
  }
}